/**
 * Account statement helpers shared across variants.
 *
 * Extracted from src/shared_assets/index.js's Account Statement subsystem (#5447
 * componentization pass). `escapeStatementHtml` previously existed as three
 * byte-identical copies (this one, plus a local `escapeHtml` duplicated inside
 * both buildBWHeaderTemplate and buildBWStatementHtml) -- consolidated to this
 * single source, re-exported as `escapeHtml` where BW's own code expects that name.
 */

export const escapeStatementHtml = (str) => {
	if (!str) return '';
	return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
};

/**
 * Build SVG chart for account statements (shared by BW and the default/Sheetz path)
 * Generates a simple area chart from time series data
 * @param {Array} timeSeriesData - Array of [timestamp, value, ...] from TimeSeriesBuilder
 * @param {Object} options - Chart options { width, height, color }
 * @returns {string} SVG markup
 */
export function buildStatementChartSvg(timeSeriesData, options = {}) {
	const width = options.width || 500;
	const height = options.height || 200;
	const color = options.color || '#299aa4';
	const padding = { top: 20, right: 20, bottom: 40, left: 60 };

	// Need at least 2 points for a chart
	if (!timeSeriesData || timeSeriesData.length < 2) {
		return '';
	}

	// Extract timestamps and values (handle both single-line and stacked formats)
	const points = timeSeriesData.map((row) => {
		const timestamp = row[0];
		// Sum all values after timestamp for stacked format, or just use row[1] for single line
		let value = 0;
		for (let i = 1; i < row.length; i++) {
			if (typeof row[i] === 'number' && !isNaN(row[i])) {
				value += row[i];
			}
		}
		return { x: timestamp, y: value };
	});

	// Calculate bounds
	const xMin = Math.min(...points.map((p) => p.x));
	const xMax = Math.max(...points.map((p) => p.x));
	const yMin = 0; // Start from 0 for area chart
	const yMax = Math.max(...points.map((p) => p.y)) * 1.1; // Add 10% headroom

	// Avoid division by zero
	const xRange = xMax - xMin || 1;
	const yRange = yMax - yMin || 1;

	// Chart dimensions
	const chartWidth = width - padding.left - padding.right;
	const chartHeight = height - padding.top - padding.bottom;

	// Scale functions
	const scaleX = (x) => padding.left + ((x - xMin) / xRange) * chartWidth;
	const scaleY = (y) => padding.top + chartHeight - ((y - yMin) / yRange) * chartHeight;

	// Build path for area chart
	const linePath = points
		.map((p, i) => {
			const x = scaleX(p.x);
			const y = scaleY(p.y);
			return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
		})
		.join(' ');

	// Close the area path
	const areaPath =
		linePath +
		` L ${scaleX(points[points.length - 1].x).toFixed(1)} ${scaleY(0).toFixed(1)}` +
		` L ${scaleX(points[0].x).toFixed(1)} ${scaleY(0).toFixed(1)} Z`;

	// Format axis labels
	const formatDate = (ts) => {
		const d = new Date(ts);
		return `${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
	};
	const formatValue = (v) => {
		if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
		if (v >= 1000) return '$' + (v / 1000).toFixed(0) + 'K';
		return '$' + v.toFixed(0);
	};

	// Generate axis ticks
	const xTicks = 5;
	const yTicks = 4;
	const xTickValues = [];
	const yTickValues = [];

	for (let i = 0; i <= xTicks; i++) {
		xTickValues.push(xMin + (xRange * i) / xTicks);
	}
	for (let i = 0; i <= yTicks; i++) {
		yTickValues.push(yMin + (yRange * i) / yTicks);
	}

	// Convert hex color to RGB components for rgba fill
	const hexToRgb = (hex) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `${r},${g},${b}`;
	};
	const colorRgb = hexToRgb(color);

	// Build SVG - use 100% width to fill container, viewBox maintains aspect ratio
	let svg = `
	<svg width="100%" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
		<defs>
			<linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
				<stop offset="0%" style="stop-color:${color};stop-opacity:0.6"/>
				<stop offset="100%" style="stop-color:${color};stop-opacity:0.15"/>
			</linearGradient>
		</defs>

		<!-- Grid lines -->
		<g stroke="#e2e8f0" stroke-width="1">
			${yTickValues.map((v) => `<line x1="${padding.left}" y1="${scaleY(v).toFixed(1)}" x2="${width - padding.right}" y2="${scaleY(v).toFixed(1)}"/>`).join('\n\t\t\t\t')}
		</g>

		<!-- Area fill -->
		<path d="${areaPath}" fill="rgba(${colorRgb},0.25)" stroke="none"/>

		<!-- Line -->
		<path d="${linePath}" fill="none" stroke="${color}" stroke-width="3"/>

		<!-- X axis labels -->
		<g fill="#666" font-size="10" text-anchor="middle">
			${xTickValues.map((v) => `<text x="${scaleX(v).toFixed(1)}" y="${height - 10}">${formatDate(v)}</text>`).join('\n\t\t\t\t')}
		</g>

		<!-- Y axis labels -->
		<g fill="#666" font-size="10" text-anchor="end">
			${yTickValues.map((v) => `<text x="${padding.left - 8}" y="${(scaleY(v) + 3).toFixed(1)}">${formatValue(v)}</text>`).join('\n\t\t\t\t')}
		</g>

		<!-- End point dot -->
		<circle cx="${scaleX(points[points.length - 1].x).toFixed(1)}" cy="${scaleY(points[points.length - 1].y).toFixed(1)}" r="5" fill="${color}"/>
	</svg>`;

	return svg;
}
